import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clinicianService, notificationService, userService, userWellnessService, wellnessAreaService, wellnessHomeworkService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import ClinicianAlerts from '../components/ClinicianAlerts';
import ClinicianInvitationManager from '../components/ClinicianInvitationManager';
import SOSButton from '../components/SOSButton';
import HomeworkAssignmentModal from '../components/HomeworkAssignmentModal';
import { Badge, Button, Card, Input } from '../components/ui';

const LICENSE_TYPE_OPTIONS = [
  'LPC',
  'LPC-S',
  'LCSW',
  'LMFT',
  'LMFT-S',
  'LCDC',
  'PsyD',
  'Psychiatrist',
  'Psychologist',
  'MD',
  'PA',
  'NP',
  'Life Coach'
];

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const buildCredentialForm = (user) => ({
  licenseNumber: user?.licenseNumber || '',
  licenseType: user?.licenseType || '',
  licenseState: user?.licenseState || '',
  licenseExpiration: formatDateInput(user?.licenseExpiration),
  city: user?.city || '',
  state: user?.state || '',
  licenses: (user?.licenses || []).map((license) => ({
    licenseNumber: license?.licenseNumber || '',
    licenseType: license?.licenseType || '',
    licenseState: license?.licenseState || '',
    licenseExpiration: formatDateInput(license?.licenseExpiration)
  }))
});

const toIsoDate = (value) => (value ? new Date(value).toISOString() : '');

const phaseNames = ['Stabilize', 'Understand', 'Rebuild', 'Sustain'];

const ClinicianDashboard = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [currentView, setCurrentView] = useState('clients');
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [inviteActionLoading, setInviteActionLoading] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [inviteForm, setInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    phone: ''
  });
  const [inviteConsent, setInviteConsent] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [credentialForm, setCredentialForm] = useState(() => buildCredentialForm(user));
  const [credentialMessage, setCredentialMessage] = useState(null);
  const [credentialSaving, setCredentialSaving] = useState(false);
  const [resendInviteLoading, setResendInviteLoading] = useState(null);
  const [showHomeworkModal, setShowHomeworkModal] = useState(false);
  const [showContactInfoModal, setShowContactInfoModal] = useState(false);
  const [selectedClientForEdit, setSelectedClientForEdit] = useState(null);
  const [contactInfoForm, setContactInfoForm] = useState({ email: '', phone: '' });
  const [contactInfoMessage, setContactInfoMessage] = useState(null);
  const [contactInfoSaving, setContactInfoSaving] = useState(false);
  const [showClientDetailsModal, setShowClientDetailsModal] = useState(false);
  const [selectedClientForDetails, setSelectedClientForDetails] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
        // Check if clinician has acknowledged guidelines
        if (user && !user.clinicianAcknowledgement?.acknowledged) {
          navigate('/clinician/acknowledge');
          return;
        }

    loadDashboardData();
  }, [user, navigate]);

  useEffect(() => {
    setCredentialForm(buildCredentialForm(user));
  }, [user]);

  const loadDashboardData = async () => {
    try {
      const [clientsResponse, statsResponse, notificationsResponse] = await Promise.all([
        clinicianService.getClients(),
        clinicianService.getDashboardStats(),
        notificationService.getNotifications()
      ]);

      setClients(clientsResponse.data.data.clients);
      setStats(statsResponse.data.data.stats);
      setNotifications(notificationsResponse.data.data.notifications || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (notificationId) => {
    setInviteActionLoading(notificationId);
    try {
      await clinicianService.acceptInvite(notificationId);
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to accept invitation:', error);
    } finally {
      setInviteActionLoading(null);
    }
  };

  const handleRemoveInvite = async (notificationId) => {
    setInviteActionLoading(notificationId);
    try {
      await clinicianService.removeInvite(notificationId);
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to remove invitation:', error);
    } finally {
      setInviteActionLoading(null);
    }
  };

  const handleClientAction = async (clientId, action) => {
    try {
      if (action === 'suspend') {
        await clinicianService.suspendClient(clientId);
      } else if (action === 'reactivate') {
        await clinicianService.reactivateClient(clientId);
      } else if (action === 'graduate') {
        await clinicianService.graduateClient(clientId);
      } else if (action === 'approveUnassign') {
        await clinicianService.approveUnassign(clientId);
      } else if (action === 'unassignSelf') {
        await clinicianService.unassignSelf(clientId);
      } else if (action === 'delete') {
        await clinicianService.deleteClient(clientId);
      }
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to update client:', error);
    }
  };

  const handleResendInvite = async (clientId) => {
    setResendInviteLoading(clientId);
    try {
      const response = await clinicianService.resendInvite(clientId, true);
      const result = response.data.data;
      
      // Show success message with details
      const smsMessage = result.smsSent ? ' (Email + SMS sent)' : ' (Email sent)';
      alert(`Invitation resent successfully!${smsMessage}\n\nTemporary Password: ${result.temporaryPassword}\n\nPlease share this with your client.`);
      
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to resend invite:', error);
      alert(error.response?.data?.message || 'Failed to resend invitation. Please try again.');
    } finally {
      setResendInviteLoading(null);
    }
  };

  const handleInviteChange = (field, value) => {
    setInviteForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openInviteModal = () => {
    setInviteStatus(null);
    setInviteResult(null);
    setShowInviteModal(true);
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteStatus(null);
    setInviteResult(null);
  };

  const handleInviteSubmit = async () => {
    setInviteStatus(null);
    if (!user?.clinicianAcknowledgement?.acknowledged) {
      setInviteStatus({
        type: 'error',
        text: 'Complete clinician acknowledgment before sending invitations.'
      });
      return;
    }
    if (!inviteConsent) {
      setInviteStatus({ type: 'error', text: 'Please confirm client consent before sending.' });
      return;
    }

    setInviteLoading(true);
    try {
      const payload = {
        firstName: inviteForm.firstName.trim(),
        lastName: inviteForm.lastName.trim(),
        email: inviteForm.email.trim(),
        username: inviteForm.username.trim() || undefined,
        phone: inviteForm.phone.trim() || undefined
      };

      const response = await clinicianService.inviteClient(payload);
      const result = response.data.data;

      setInviteResult({
        email: result.client.email,
        username: result.client.username,
        tempPassword: result.temporaryPassword
      });
      let copyMessage = '';
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(result.temporaryPassword);
          copyMessage = ' Temporary password copied to clipboard.';
        }
      } catch (copyError) {
        copyMessage = ' Copy to clipboard failed; please copy manually.';
      }
      setInviteStatus({ type: 'success', text: `Invitation sent successfully.${copyMessage}` });
      setInviteForm({ firstName: '', lastName: '', email: '', username: '', phone: '' });
      setInviteConsent(false);
      await loadDashboardData();
    } catch (error) {
      setInviteStatus({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send invitation.'
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCredentialChange = (field, value) => {
    setCredentialForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdditionalLicenseChange = (index, field, value) => {
    setCredentialForm((prev) => {
      const updatedLicenses = [...prev.licenses];
      updatedLicenses[index] = {
        ...updatedLicenses[index],
        [field]: value
      };
      return {
        ...prev,
        licenses: updatedLicenses
      };
    });
  };

  const handleAddLicense = () => {
    setCredentialForm((prev) => ({
      ...prev,
      licenses: [
        ...prev.licenses,
        {
          licenseNumber: '',
          licenseType: '',
          licenseState: '',
          licenseExpiration: ''
        }
      ]
    }));
  };

  const handleRemoveLicense = (index) => {
    setCredentialForm((prev) => ({
      ...prev,
      licenses: prev.licenses.filter((_, licenseIndex) => licenseIndex !== index)
    }));
  };

  const handleSaveCredentials = async () => {
    setCredentialMessage(null);

    const hasPartialLicense = credentialForm.licenses.some((license) => {
      const fields = [license.licenseType, license.licenseNumber, license.licenseState, license.licenseExpiration];
      const filled = fields.filter(Boolean).length;
      return filled > 0 && filled < fields.length;
    });

    if (hasPartialLicense) {
      setCredentialMessage({
        type: 'error',
        text: 'Complete all fields for each additional license or remove the entry.'
      });
      return;
    }

    setCredentialSaving(true);
    try {
      const payload = {
        licenseNumber: credentialForm.licenseNumber.trim(),
        licenseType: credentialForm.licenseType,
        licenseState: credentialForm.licenseState.trim().toUpperCase(),
        licenseExpiration: toIsoDate(credentialForm.licenseExpiration),
        city: credentialForm.city.trim(),
        state: credentialForm.state.trim().toUpperCase(),
        licenses: credentialForm.licenses
          .filter((license) => license.licenseType && license.licenseNumber && license.licenseState && license.licenseExpiration)
          .map((license) => ({
            licenseNumber: license.licenseNumber.trim(),
            licenseType: license.licenseType,
            licenseState: license.licenseState.trim().toUpperCase(),
            licenseExpiration: toIsoDate(license.licenseExpiration)
          }))
      };

      const response = await userService.updateProfile(user.id, payload);
      updateUser(response.data.data.user);
      setCredentialMessage({ type: 'success', text: 'Credentials updated successfully.' });
    } catch (error) {
      setCredentialMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to update credentials.'
      });
    } finally {
      setCredentialSaving(false);
    }
  };

  const openContactInfoModal = (client) => {
    setSelectedClientForEdit(client);
    setContactInfoForm({
      email: client.email || '',
      phone: client.phone || ''
    });
    setContactInfoMessage(null);
    setShowContactInfoModal(true);
  };

  const closeContactInfoModal = () => {
    setShowContactInfoModal(false);
    setSelectedClientForEdit(null);
    setContactInfoForm({ email: '', phone: '' });
    setContactInfoMessage(null);
  };

  const openClientDetailsModal = (client) => {
    setSelectedClientForDetails(client);
    setShowClientDetailsModal(true);
  };

  const closeClientDetailsModal = () => {
    setShowClientDetailsModal(false);
    setSelectedClientForDetails(null);
  };

  const handleContactInfoChange = (field, value) => {
    setContactInfoForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveContactInfo = async () => {
    setContactInfoMessage(null);

    if (!contactInfoForm.email && !contactInfoForm.phone) {
      setContactInfoMessage({
        type: 'error',
        text: 'Please provide at least email or phone number.'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (contactInfoForm.email && !emailRegex.test(contactInfoForm.email)) {
      setContactInfoMessage({
        type: 'error',
        text: 'Please enter a valid email address.'
      });
      return;
    }

    setContactInfoSaving(true);
    try {
      await clinicianService.updateClientContactInfo(selectedClientForEdit._id, {
        email: contactInfoForm.email.trim() || undefined,
        phone: contactInfoForm.phone.trim() || undefined
      });

      setContactInfoMessage({ type: 'success', text: 'Contact information updated successfully.' });
      await loadDashboardData();
      setTimeout(() => closeContactInfoModal(), 1000);
    } catch (error) {
      setContactInfoMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to update contact information.'
      });
    } finally {
      setContactInfoSaving(false);
    }
  };

  // Dropdown menu component
  const ActionsDropdown = ({ client }) => {
    const isOpen = openDropdown === client._id;
    
    return (
      <div className="relative" ref={isOpen ? dropdownRef : null}>
        <button
          onClick={() => setOpenDropdown(isOpen ? null : client._id)}
          className="inline-flex items-center gap-1 rounded-lg border-2 border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 hover:border-neutral-400 transition-colors shadow-sm"
          title="More Actions"
        >
          ⋮ More
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isOpen && (
          <div className="absolute right-0 z-50 mt-1 w-56 rounded-soft border border-neutral-200 bg-white shadow-xl">
            <div className="py-1">
              
              <button
                onClick={() => {
                  openContactInfoModal(client);
                  setOpenDropdown(null);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
              >
                ✏️ Edit Contact Info
              </button>
              
              <button
                onClick={() => {
                  if (window.confirm('Resend invitation email and SMS to this client? A new temporary password will be generated.')) {
                    handleResendInvite(client._id);
                    setOpenDropdown(null);
                  }
                }}
                disabled={resendInviteLoading === client._id}
                className="block w-full px-4 py-2 text-left text-sm text-primary-700 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendInviteLoading === client._id ? '📧 Sending...' : '📧 Resend Invite'}
              </button>
              
              <button
                onClick={() => {
                  navigate(`/clinician/clients/${client._id}/journal`);
                  setOpenDropdown(null);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
              >
                📋 View Journal
              </button>
              
              {client.unassignRequestedAt && (
                <button
                  onClick={() => {
                    if (window.confirm('Approve unassignment? This will remove the client from your list.')) {
                      handleClientAction(client._id, 'approveUnassign');
                      setOpenDropdown(null);
                    }
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-warning-700 hover:bg-warning-50"
                >
                  ✅ Approve Unassignment
                </button>
              )}
              
              {client.isActive ? (
                <button
                  onClick={() => {
                    handleClientAction(client._id, 'suspend');
                    setOpenDropdown(null);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                >
                  ⏸️ Suspend Client
                </button>
              ) : (
                <button
                  onClick={() => {
                    handleClientAction(client._id, 'reactivate');
                    setOpenDropdown(null);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-success-700 hover:bg-success-50"
                >
                  ▶️ Reactivate Client
                </button>
              )}
              
              <button
                onClick={() => {
                  handleClientAction(client._id, 'graduate');
                  setOpenDropdown(null);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-primary-700 hover:bg-primary-50"
              >
                🎓 Graduate Client
              </button>
              
              <div className="border-t border-neutral-200 my-1"></div>
              
              <button
                onClick={() => {
                  if (window.confirm('Remove yourself from this client? The client account will remain active but unassigned.')) {
                    handleClientAction(client._id, 'unassignSelf');
                    setOpenDropdown(null);
                  }
                }}
                className="block w-full px-4 py-2 text-left text-sm text-warning-700 hover:bg-warning-50"
              >
                👋 Remove Myself
              </button>
              
              <button
                onClick={() => {
                  if (window.confirm('Delete this client? This will disable their access.')) {
                    handleClientAction(client._id, 'delete');
                    setOpenDropdown(null);
                  }
                }}
                className="block w-full px-4 py-2 text-left text-sm text-error-700 hover:bg-error-50"
              >
                🗑️ Delete Client
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  const inviteNotifications = notifications.filter(
    (notification) => notification.type === 'clinician_invite' && notification.status === 'unread'
  );
  const hasConsent = (client) => Boolean(
    client?.consentToShare ||
    client?.consent?.shareEngagement ||
    client?.consent?.shareMoodCraving ||
    client?.consent?.shareJournalEntries ||
    client?.consent?.shareFaithEntries
  );

  return (
    <>
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 px-4">
          <Card className="w-full max-w-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-display font-semibold text-neutral-900">Invite Client</h3>
                <p className="mt-1 text-sm text-neutral-600">
                  Send a non-marketing enrollment email with temporary credentials.
                </p>
              </div>
              <Button variant="ghost" onClick={closeInviteModal}>Close</Button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Input
                label="First name"
                value={inviteForm.firstName}
                onChange={(event) => handleInviteChange('firstName', event.target.value)}
                fullWidth
              />
              <Input
                label="Last name"
                value={inviteForm.lastName}
                onChange={(event) => handleInviteChange('lastName', event.target.value)}
                fullWidth
              />
              <Input
                label="Email"
                type="email"
                value={inviteForm.email}
                onChange={(event) => handleInviteChange('email', event.target.value)}
                fullWidth
              />
              <Input
                label="Username (optional)"
                value={inviteForm.username}
                onChange={(event) => handleInviteChange('username', event.target.value)}
                fullWidth
              />
              <Input
                label="Phone (optional)"
                value={inviteForm.phone}
                onChange={(event) => handleInviteChange('phone', event.target.value)}
                fullWidth
              />
            </div>

            {!user?.clinicianAcknowledgement?.acknowledged && (
              <div className="mt-4 rounded-soft border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
                Clinician acknowledgment is required before inviting clients.
              </div>
            )}

            <label className="mt-4 flex items-start gap-3 text-sm text-neutral-600">
              <input
                type="checkbox"
                className="mt-1"
                checked={inviteConsent}
                onChange={(event) => setInviteConsent(event.target.checked)}
              />
              I confirm this client has consented to receive this enrollment email.
            </label>

            {inviteStatus && (
              <div
                className={`mt-4 rounded-soft border p-3 text-sm ${
                  inviteStatus.type === 'success'
                    ? 'border-success-200 bg-success-50 text-success-700'
                    : 'border-error-200 bg-error-50 text-error-700'
                }`}
              >
                {inviteStatus.text}
              </div>
            )}

            {inviteResult && (
              <div className="mt-4 rounded-soft border border-neutral-200 bg-neutral-50 p-3 text-sm">
                <p className="font-semibold text-neutral-800">Temporary credentials</p>
                <p className="mt-2 text-neutral-700">Email: {inviteResult.email}</p>
                <p className="text-neutral-700">Username: {inviteResult.username}</p>
                <p className="text-neutral-700">Temporary Password: {inviteResult.tempPassword}</p>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <Button variant="secondary" onClick={closeInviteModal}>
                Cancel
              </Button>
              <Button
                onClick={handleInviteSubmit}
                disabled={inviteLoading || !user?.clinicianAcknowledgement?.acknowledged}
              >
                {inviteLoading ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showContactInfoModal && selectedClientForEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 px-4">
          <Card className="w-full max-w-md">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-display font-semibold text-neutral-900">Edit Contact Information</h3>
                <p className="mt-1 text-sm text-neutral-600">
                  Update email or phone for {selectedClientForEdit.firstName} {selectedClientForEdit.lastName}
                </p>
              </div>
              <Button variant="ghost" onClick={closeContactInfoModal}>Close</Button>
            </div>

            <div className="mt-4 grid gap-4">
              <Input
                label="Email"
                type="email"
                value={contactInfoForm.email}
                onChange={(event) => handleContactInfoChange('email', event.target.value)}
                fullWidth
              />
              <Input
                label="Phone"
                type="tel"
                value={contactInfoForm.phone}
                onChange={(event) => handleContactInfoChange('phone', event.target.value)}
                fullWidth
              />
            </div>

            {contactInfoMessage && (
              <div
                className={`mt-4 rounded-soft border p-3 text-sm ${
                  contactInfoMessage.type === 'success'
                    ? 'border-success-200 bg-success-50 text-success-700'
                    : 'border-error-200 bg-error-50 text-error-700'
                }`}
              >
                {contactInfoMessage.text}
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <Button variant="secondary" onClick={closeContactInfoModal} disabled={contactInfoSaving}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveContactInfo}
                disabled={contactInfoSaving}
              >
                {contactInfoSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showClientDetailsModal && selectedClientForDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 px-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h3 className="text-xl font-display font-semibold text-neutral-900">Client Details</h3>
                <p className="mt-1 text-sm text-neutral-600">
                  {selectedClientForDetails.firstName} {selectedClientForDetails.lastName}
                </p>
              </div>
              <Button variant="ghost" onClick={closeClientDetailsModal}>Close</Button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-neutral-500 uppercase">Name</p>
                  <p className="mt-1 text-sm font-medium text-neutral-900">{`${selectedClientForDetails.firstName} ${selectedClientForDetails.lastName}`}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-neutral-500 uppercase">Username</p>
                  <p className="mt-1 text-sm font-medium text-neutral-900">{selectedClientForDetails.username || '—'}</p>
                </div>
              </div>

              {/* Current Status */}
              <div className="border-t border-neutral-200 pt-4">
                <p className="text-xs font-semibold text-neutral-500 uppercase mb-3">Program Status</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-neutral-600">Current Day</p>
                    <p className="mt-1 text-lg font-semibold text-neutral-900">{selectedClientForDetails.currentDay}/365</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-600">Phase</p>
                    <div className="mt-1">
                      <Badge variant={`phase.${phaseNames[selectedClientForDetails.currentPhase - 1]?.toLowerCase()}`} size="sm">
                        Phase {selectedClientForDetails.currentPhase}: {phaseNames[selectedClientForDetails.currentPhase - 1]}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Status */}
              <div className="border-t border-neutral-200 pt-4">
                <p className="text-xs font-semibold text-neutral-500 uppercase mb-3">Account Status</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-neutral-600">Status</p>
                    <div className="mt-1">
                      <Badge variant={selectedClientForDetails.isActive ? 'success' : 'neutral'} size="sm">
                        {selectedClientForDetails.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {selectedClientForDetails.unassignRequestedAt && (
                        <Badge variant="warning" size="sm" className="ml-2">
                          Unassignment Requested
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-600">Profile Created</p>
                    <p className="mt-1 text-sm font-medium text-neutral-900">
                      {selectedClientForDetails.isVerified && selectedClientForDetails.lastLogin ? (
                        <Badge variant="success" size="sm">Yes</Badge>
                      ) : (
                        <Badge variant="warning" size="sm">Pending</Badge>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Invitation Status */}
              <div className="border-t border-neutral-200 pt-4">
                <p className="text-xs font-semibold text-neutral-500 uppercase mb-3">Invitation Status</p>
                <div>
                  <p className="text-xs text-neutral-600 mb-1">Invited</p>
                  <div>
                    {selectedClientForDetails.invitationSentAt ? (
                      <div>
                        <Badge variant="success" size="sm">Yes</Badge>
                        <p className="mt-2 text-xs text-neutral-500">
                          Sent on {new Date(selectedClientForDetails.invitationSentAt).toLocaleDateString()}
                        </p>
                      </div>
                    ) : (
                      <Badge variant="neutral" size="sm">No</Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Consent Status */}
              <div className="border-t border-neutral-200 pt-4">
                <p className="text-xs font-semibold text-neutral-500 uppercase mb-3">Consent Status</p>
                <div>
                  <Badge variant={hasConsent(selectedClientForDetails) ? 'success' : 'neutral'} size="sm">
                    {hasConsent(selectedClientForDetails) ? 'Consent Provided' : 'No Consent'}
                  </Badge>
                  {hasConsent(selectedClientForDetails) && (
                    <div className="mt-2 space-y-1 text-xs text-neutral-600">
                      {selectedClientForDetails.consent?.shareEngagement && <p>✓ Share Engagement</p>}
                      {selectedClientForDetails.consent?.shareMoodCraving && <p>✓ Share Mood & Craving</p>}
                      {selectedClientForDetails.consent?.shareJournalEntries && <p>✓ Share Journal Entries</p>}
                      {selectedClientForDetails.consent?.shareFaithEntries && <p>✓ Share Faith Entries</p>}
                      {selectedClientForDetails.consentToShare && <p>✓ General Consent</p>}
                    </div>
                  )}
                </div>
              </div>

              {/* Last Activity */}
              <div className="border-t border-neutral-200 pt-4">
                <p className="text-xs font-semibold text-neutral-500 uppercase mb-3">Activity</p>
                <div>
                  <p className="text-xs text-neutral-600">Last Login</p>
                  <p className="mt-1 text-sm font-medium text-neutral-900">
                    {selectedClientForDetails.lastLogin
                      ? new Date(selectedClientForDetails.lastLogin).toLocaleDateString()
                      : '—'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 border-t border-neutral-200 pt-4 flex gap-3">
              <Button variant="secondary" onClick={closeClientDetailsModal}>
                Close
              </Button>
              <Button
                onClick={() => {
                  navigate(`/clinician/clients/${selectedClientForDetails._id}/profile`);
                  closeClientDetailsModal();
                }}
              >
                View Full Profile →
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Navigation />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <Badge variant="primary" size="sm">Clinician Workspace</Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            {currentView === 'clients' ? 'Clinician Dashboard' : 'Invitations'}
          </h2>
          <p className="mt-2 text-neutral-600">
            Welcome, {user.title ? `${user.title} ${user.lastName}` : `${user.firstName} ${user.lastName}`}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 flex gap-3">
          <Button
            onClick={() => setCurrentView('clients')}
            variant={currentView === 'clients' ? 'primary' : 'secondary'}
          >
            👥 My Clients
          </Button>
          <Button
            onClick={() => setCurrentView('invitations')}
            variant={currentView === 'invitations' ? 'primary' : 'secondary'}
          >
            ✉️ Invitations
          </Button>
        </div>

        {currentView === 'invitations' ? (
          <ClinicianInvitationManager />
        ) : (
          <>
        <Card className="mb-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm text-neutral-500">Clinician Profile</p>
              <p className="mt-1 text-lg font-semibold text-neutral-900">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-sm text-neutral-600">{user.email}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="primary"
                onClick={openInviteModal}
                className="shadow-md shadow-primary-500/25 ring-2 ring-primary-300/60"
              >
                Invite Client
              </Button>
              <Button variant="primary" onClick={() => setShowHomeworkModal(true)}>
                📚 Assign Homework
              </Button>
              <Button variant="primary" onClick={() => navigate('/clinician/messages')}>
                Encouragement Messages
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-500">License Type</p>
                <p className="text-sm font-semibold text-neutral-900">{user.licenseType || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-500">License Number</p>
                <p className="text-sm font-semibold text-neutral-900">{user.licenseNumber || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-500">License State</p>
                <p className="text-sm font-semibold text-neutral-900">{user.licenseState || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-500">Expiration</p>
                <p className="text-sm font-semibold text-neutral-900">
                  {user.licenseExpiration ? new Date(user.licenseExpiration).toLocaleDateString() : 'Not provided'}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-display font-semibold text-neutral-900">Credentials</h3>
              <p className="text-sm text-neutral-600">Update your license details and practice location.</p>
            </div>
            <Button
              variant="secondary"
              onClick={handleSaveCredentials}
              disabled={credentialSaving}
            >
              {credentialSaving ? 'Saving...' : 'Save Credentials'}
            </Button>
          </div>

          {credentialMessage && (
            <div
              className={`mt-4 rounded-soft border p-3 text-sm ${
                credentialMessage.type === 'success'
                  ? 'border-success-200 bg-success-50 text-success-700'
                  : 'border-error-200 bg-error-50 text-error-700'
              }`}
            >
              {credentialMessage.text}
            </div>
          )}

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Input
              label="License Number"
              value={credentialForm.licenseNumber}
              onChange={(event) => handleCredentialChange('licenseNumber', event.target.value)}
              fullWidth
            />
            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">License Type</span>
              <select
                value={credentialForm.licenseType}
                onChange={(event) => handleCredentialChange('licenseType', event.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="">Select license type</option>
                {LICENSE_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>
            <Input
              label="License State (2-letter code)"
              value={credentialForm.licenseState}
              onChange={(event) => handleCredentialChange('licenseState', event.target.value)}
              fullWidth
            />
            <Input
              label="License Expiration"
              type="date"
              value={credentialForm.licenseExpiration}
              onChange={(event) => handleCredentialChange('licenseExpiration', event.target.value)}
              fullWidth
            />
            <Input
              label="City"
              value={credentialForm.city}
              onChange={(event) => handleCredentialChange('city', event.target.value)}
              fullWidth
            />
            <Input
              label="State (2-letter code)"
              value={credentialForm.state}
              onChange={(event) => handleCredentialChange('state', event.target.value)}
              fullWidth
            />
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between gap-4">
              <h4 className="text-sm font-semibold text-neutral-800">Additional Licenses</h4>
              <Button size="sm" variant="ghost" onClick={handleAddLicense}>Add License</Button>
            </div>
            <div className="mt-3 space-y-4">
              {credentialForm.licenses.length === 0 && (
                <p className="text-xs text-neutral-500">No additional licenses added.</p>
              )}
              {credentialForm.licenses.map((license, index) => (
                <div key={`license-${index}`} className="rounded-soft border border-neutral-200 p-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="License Number"
                      value={license.licenseNumber}
                      onChange={(event) => handleAdditionalLicenseChange(index, 'licenseNumber', event.target.value)}
                      fullWidth
                    />
                    <label className="text-sm text-neutral-700">
                      <span className="mb-2 block font-medium">License Type</span>
                      <select
                        value={license.licenseType}
                        onChange={(event) => handleAdditionalLicenseChange(index, 'licenseType', event.target.value)}
                        className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                      >
                        <option value="">Select license type</option>
                        {LICENSE_TYPE_OPTIONS.map((type) => (
                          <option key={`${type}-${index}`} value={type}>{type}</option>
                        ))}
                      </select>
                    </label>
                    <Input
                      label="License State"
                      value={license.licenseState}
                      onChange={(event) => handleAdditionalLicenseChange(index, 'licenseState', event.target.value)}
                      fullWidth
                    />
                    <Input
                      label="License Expiration"
                      type="date"
                      value={license.licenseExpiration}
                      onChange={(event) => handleAdditionalLicenseChange(index, 'licenseExpiration', event.target.value)}
                      fullWidth
                    />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveLicense(index)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card colorVariant="indigo">
            <p className="text-sm text-indigo-600 font-medium">Total Clients</p>
            <p className="mt-2 text-3xl font-bold text-indigo-700">{stats?.totalClients || 0}</p>
          </Card>
          <Card colorVariant="emerald">
            <p className="text-sm text-emerald-600 font-medium">Active Clients</p>
            <p className="mt-2 text-3xl font-bold text-emerald-700">{stats?.activeClients || 0}</p>
          </Card>
          <Card colorVariant="cyan">
            <p className="text-sm text-cyan-600 font-medium">Average Day</p>
            <p className="mt-2 text-3xl font-bold text-cyan-700">{stats?.averageDay || 0}</p>
          </Card>
          <Card colorVariant="amber">
            <p className="text-sm text-amber-600 font-medium">Phase 4 Clients</p>
            <p className="mt-2 text-3xl font-bold text-amber-700">{stats?.clientsInPhase?.[4] || 0}</p>
          </Card>
        </div>

        {inviteNotifications.length > 0 && (
          <Card colorVariant="violet" borderAccent className="mt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-display font-semibold text-neutral-800">Client Invitations</h3>
              <Badge variant="secondary">{inviteNotifications.length} pending</Badge>
            </div>
            <div className="mt-4 space-y-3">
              {inviteNotifications.map((invite) => (
                <div
                  key={invite._id}
                  className="flex flex-col gap-3 rounded-soft border border-neutral-200 p-3 md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">{invite.metadata?.clientName || 'Client invitation'}</p>
                    <p className="text-xs text-neutral-600">{invite.metadata?.clientEmail || 'Email not provided'}</p>
                    <p className="text-xs text-neutral-500">{invite.message}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAcceptInvite(invite._id)}
                    disabled={inviteActionLoading === invite._id}
                  >
                    {inviteActionLoading === invite._id ? 'Accepting...' : 'Accept Invite'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveInvite(invite._id)}
                    disabled={inviteActionLoading === invite._id}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="mt-8">
          <h3 className="text-2xl font-display font-semibold text-neutral-900 mb-4">Clinical Alerts</h3>
          <ClinicianAlerts selectedTab="active" />
        </div>

        <Card className="mt-8 overflow-visible">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-display font-semibold text-neutral-800">My Clients</h3>
            <Badge variant="secondary">{clients.length} total</Badge>
          </div>

          {/* Mobile card list — visible on small screens */}
          <div className="md:hidden mt-4 space-y-3 pb-20">
            {clients.length === 0 ? (
              <p className="py-6 text-center text-neutral-500">No clients assigned yet</p>
            ) : (
              clients.map((client) => (
                <div key={client._id} className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-neutral-900 text-sm">{`${client.firstName} ${client.lastName}`}</p>
                      <p className="text-xs text-neutral-500">{client.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-neutral-600">
                    <span>{client.phone || '—'}</span>
                    <span>•</span>
                    <span>Day {client.currentDay}</span>
                  </div>
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    <button
                      onClick={() => openClientDetailsModal(client)}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border-2 border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 transition-colors"
                      title="View Client Details"
                    >
                      ℹ️ Details
                    </button>
                    <button
                      onClick={() => navigate(`/clinician/clients/${client._id}/profile`)}
                      className="flex-1 min-w-fit inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors"
                    >
                      👤 Profile
                    </button>
                    <button
                      onClick={() => openContactInfoModal(client)}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border-2 border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 hover:border-neutral-400 transition-colors"
                      title="Edit Contact Info"
                    >
                      ✏️
                    </button>
                    <ActionsDropdown client={client} />
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop table — hidden on small screens */}
          <div className="hidden md:block mt-4 overflow-x-auto pb-20">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-neutral-200 text-neutral-500">
                <tr>
                  <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Name</th>
                  <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Email</th>
                  <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Phone</th>
                  <th className="whitespace-nowrap py-1.5 pr-3 text-center font-medium">Current Day</th>
                  <th className="whitespace-nowrap py-1.5 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {clients.map((client) => (
                  <tr key={client._id} className="hover:bg-neutral-50">
                    <td className="whitespace-nowrap py-1.5 pr-3 font-medium text-neutral-800">{`${client.firstName} ${client.lastName}`}</td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">{client.email}</td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">{client.phone || '—'}</td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-center text-neutral-600">{client.currentDay}</td>
                    <td className="whitespace-nowrap py-1.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openClientDetailsModal(client)}
                          className="inline-flex items-center gap-1 rounded-lg border-2 border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 transition-colors shadow-sm"
                          title="View Client Details"
                        >
                          ℹ️ Details
                        </button>
                        <button
                          onClick={() => navigate(`/clinician/clients/${client._id}/profile`)}
                          className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors shadow-sm"
                          title="View Full Profile"
                        >
                          👤 Profile
                        </button>
                        <ActionsDropdown client={client} />
                      </div>
                    </td>
                  </tr>
                ))}
                {clients.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-neutral-500">
                      No clients assigned yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* end hidden md:block desktop table */}
        </Card>
          </>
        )}
      </main>

      <SOSButton variant="floating" />

      {/* Homework Assignment Modal */}
      <HomeworkAssignmentModal
        isOpen={showHomeworkModal}
        onClose={() => setShowHomeworkModal(false)}
        clients={clients}
        onAssignSuccess={loadDashboardData}
      />

      <ConditionBasedHomeworkPanel clients={clients} />
    </>
  );
};

// Condition-based (wellness-area) homework workflow for clinicians.
// Renders a selector for a client, loads a library filtered by the
// client's clinician-designated wellness areas, and assigns with optional
// private notes. Also lists active assignments (metadata only — never the
// client's reflection text).
const ConditionBasedHomeworkPanel = ({ clients }) => {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [wellnessAreas, setWellnessAreas] = useState([]);
  const [clientAreaIds, setClientAreaIds] = useState([]);
  const [clientAreaSource, setClientAreaSource] = useState('user-fallback');
  const [clientAreasLoading, setClientAreasLoading] = useState(false);
  const [clientAreasSaving, setClientAreasSaving] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assigningId, setAssigningId] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    wellnessAreaService.list()
      .then((res) => setWellnessAreas(res.data?.data || []))
      .catch((err) => console.error('wellness areas load failed', err));
  }, []);

  useEffect(() => {
    if (!selectedAreaId) {
      setLibrary([]);
      return;
    }
    const slug = wellnessAreas.find((a) => a._id === selectedAreaId)?.slug;
    if (!slug) return;
    setLibraryLoading(true);
    wellnessHomeworkService.getLibrary({ wellnessArea: slug, limit: 30 })
      .then((res) => setLibrary(res.data?.data || []))
      .catch((err) => console.error('library load failed', err))
      .finally(() => setLibraryLoading(false));
  }, [selectedAreaId, wellnessAreas]);

  useEffect(() => {
    if (!selectedClientId) {
      setClientAreaIds([]);
      setClientAreaSource('user-fallback');
      setSelectedAreaId('');
      return;
    }

    setClientAreasLoading(true);
    userWellnessService.getClinicianAreas(selectedClientId)
      .then((res) => {
        const payload = res.data?.data || {};
        const effective = Array.isArray(payload.effectiveWellnessAreas)
          ? payload.effectiveWellnessAreas
          : [];
        const nextIds = effective
          .map((a) => (typeof a === 'string' ? a : a._id))
          .filter(Boolean);

        setClientAreaIds(nextIds);
        setClientAreaSource(payload.source || 'user-fallback');
        setSelectedAreaId(nextIds[0] || '');
      })
      .catch((err) => {
        console.error('client wellness areas load failed', err);
        setClientAreaIds([]);
        setClientAreaSource('user-fallback');
        setSelectedAreaId('');
      })
      .finally(() => setClientAreasLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) {
      setActiveAssignments([]);
      return;
    }
    setActiveLoading(true);
    wellnessHomeworkService.getActiveAssignments(selectedClientId)
      .then((res) => setActiveAssignments(res.data?.data || []))
      .catch((err) => console.error('active assignments failed', err))
      .finally(() => setActiveLoading(false));
  }, [selectedClientId]);

  const handleAssign = async (item) => {
    if (!selectedClientId || !selectedAreaId) return;
    setAssigningId(item._id);
    setStatusMessage(null);
    try {
      const payload = {
        clientUserId: selectedClientId,
        homeworkItemId: item._id,
        wellnessAreaId: selectedAreaId,
        clinicianNotes: notesDraft || undefined,
        dueDate: dueDate || undefined
      };
      await wellnessHomeworkService.assignForClient(payload);
      const client = clients?.find((c) => c._id === selectedClientId);
      const name = client ? `${client.firstName} ${client.lastName}` : 'the client';
      setStatusMessage({ type: 'success', text: `Assigned "${item.title}" to ${name}.` });
      setNotesDraft('');
      const refresh = await wellnessHomeworkService.getActiveAssignments(selectedClientId);
      setActiveAssignments(refresh.data?.data || []);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to assign homework.';
      setStatusMessage({ type: 'error', text: msg });
    } finally {
      setAssigningId(null);
    }
  };

  const handleApplyAreaToClient = async () => {
    if (!selectedClientId || !selectedAreaId) return;

    const nextAreaIds = Array.from(new Set([...clientAreaIds, selectedAreaId]));
    setClientAreasSaving(true);
    setStatusMessage(null);
    try {
      await userWellnessService.updateAreas(selectedClientId, nextAreaIds);
      setClientAreaIds(nextAreaIds);
      setClientAreaSource('clinician-client');
      setStatusMessage({ type: 'success', text: 'Client focus areas updated for condition-based matching.' });
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to update client focus areas.';
      setStatusMessage({ type: 'error', text: msg });
    } finally {
      setClientAreasSaving(false);
    }
  };

  const scopedAreas = clientAreaIds.length > 0
    ? wellnessAreas.filter((a) => clientAreaIds.includes(a._id))
    : wellnessAreas;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      <Card className="mt-8">
        <h3 className="text-xl font-display font-semibold text-neutral-800">
          Assign Condition-Based Homework
        </h3>
        <p className="mt-2 text-sm text-neutral-500">
          Match exercises to a client's wellness areas. Your private notes are encrypted and never shown to the client.
        </p>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-neutral-700">Client</span>
            <select
              className="mt-1 block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              <option value="">Select a client…</option>
              {(clients || []).map((c) => (
                <option key={c._id} value={c._id}>
                  {c.firstName} {c.lastName}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-neutral-700">Wellness area</span>
            <select
              className="mt-1 block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={selectedAreaId}
              onChange={(e) => setSelectedAreaId(e.target.value)}
              disabled={clientAreasLoading}
            >
              <option value="">Select an area…</option>
              {scopedAreas.map((a) => (
                <option key={a._id} value={a._id}>{a.displayName}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-neutral-500">
              {clientAreasLoading && 'Loading client-matched areas...'}
              {!clientAreasLoading && clientAreaSource === 'clinician-client' && 'Using clinician-designated areas from ClinicianClient.'}
              {!clientAreasLoading && clientAreaSource !== 'clinician-client' && 'Using user wellness areas fallback (no clinician-specific set yet).'}
            </p>
            <Button
              size="sm"
              variant="ghost"
              className="mt-2"
              disabled={!selectedClientId || !selectedAreaId || clientAreasSaving}
              onClick={handleApplyAreaToClient}
            >
              {clientAreasSaving ? 'Saving...' : 'Add Area To Client Focus'}
            </Button>
          </label>

          <label className="block md:col-span-2">
            <span className="text-sm font-medium text-neutral-700">Private clinician notes (optional)</span>
            <textarea
              className="mt-1 block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              rows={2}
              maxLength={2000}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Not shown to the client."
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-neutral-700">Due date (optional)</span>
            <input
              type="date"
              className="mt-1 block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </label>
        </div>

        {statusMessage && (
          <div className={`mt-4 rounded-md px-3 py-2 text-sm ${
            statusMessage.type === 'success'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-coral-50 text-coral-700 border border-coral-200'
          }`}>
            {statusMessage.text}
          </div>
        )}

        <div className="mt-6">
          <h4 className="text-md font-semibold text-neutral-800">Library</h4>
          {!selectedAreaId && (
            <p className="mt-2 text-sm text-neutral-500">Select a wellness area to browse matching exercises.</p>
          )}
          {libraryLoading && <p className="mt-2 text-sm text-neutral-500">Loading…</p>}
          {!libraryLoading && selectedAreaId && library.length === 0 && (
            <p className="mt-2 text-sm text-neutral-500">No exercises for this area yet.</p>
          )}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {library.map((item) => (
              <Card key={item._id} variant="feature" padding="sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-neutral-800">{item.title}</p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {item.pillar} · {item.difficultyLevel} · ~{item.estimatedMinutes} min
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="clinician"
                    disabled={!selectedClientId || assigningId === item._id}
                    onClick={() => handleAssign(item)}
                  >
                    {assigningId === item._id ? 'Assigning…' : 'Assign'}
                  </Button>
                </div>
                <p className="text-sm text-neutral-700 mt-2 line-clamp-3">{item.instructions}</p>
              </Card>
            ))}
          </div>
        </div>

        {selectedClientId && (
          <div className="mt-8">
            <h4 className="text-md font-semibold text-neutral-800">Client's active assignments</h4>
            {activeLoading && <p className="mt-2 text-sm text-neutral-500">Loading…</p>}
            {!activeLoading && activeAssignments.length === 0 && (
              <p className="mt-2 text-sm text-neutral-500">No active assignments.</p>
            )}
            <div className="mt-3 space-y-2">
              {activeAssignments.map((a) => (
                <div key={a._id} className="flex items-center justify-between text-sm py-2 border-b border-border">
                  <div>
                    <span className="font-medium">{a.homeworkItemId?.title || 'Exercise'}</span>
                    <span className="text-neutral-500 ml-2">· {a.wellnessAreaId?.displayName}</span>
                  </div>
                  <Badge variant={a.status === 'in-progress' ? 'primary' : 'secondary'}>
                    {a.status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </main>
  );
};

export default ClinicianDashboard;
